/**
 * ProfileEditModal — create/edit a runner policy profile + assign runners.
 */

import { useState, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { Button } from '@/components/ui';
import { useSaveProfile } from '@/features/mcp/hooks/usePolicyLayers';
import type { PolicyProfile, RunnerPolicyConfig } from '@/features/mcp/hooks/usePolicyLayers';
import { RunnerPolicyForm } from './RunnerPolicyForm';

export function ProfileEditModal({
  profile,
  runners,
  initialConfig,
  onClose,
}: {
  profile: PolicyProfile | null;
  runners: { agent_id: string; name: string; status: string }[];
  /** Values to preload when creating a new profile (populated from global). */
  initialConfig?: RunnerPolicyConfig;
  onClose: () => void;
}) {
  const saveProfile = useSaveProfile();
  const [name, setName] = useState(profile?.name ?? '');
  const [config, setConfig] = useState<RunnerPolicyConfig>({
    ...(profile?.config ?? initialConfig ?? {}),
  });
  const [runnerIds, setRunnerIds] = useState<string[]>(profile?.runner_ids ?? []);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  // ADR-114 enmienda 1: baseline del config (inicializado desde el perfil).
  const initialConfigRef = useRef<string>(JSON.stringify(config));
  const isDirty =
    name !== (profile?.name ?? '') ||
    JSON.stringify(runnerIds) !== JSON.stringify(profile?.runner_ids ?? []) ||
    JSON.stringify(config) !== initialConfigRef.current;
  const discardLabels = {
    title: 'Discard changes?',
    message: 'You have unsaved changes. If you close now, they will be lost.',
    confirm: 'Discard',
    cancel: 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    await saveProfile.mutateAsync({
      id: profile?.id,
      name: name.trim() || null,
      config,
      runner_ids: runnerIds,
    });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) handleClose(); }}>
      <DialogContent
        className="max-w-3xl"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{profile ? 'Editar perfil' : 'Nuevo perfil'}</h2>
        </div>

        <div className="space-y-1">
          <span className="text-sm font-medium">Nombre del perfil</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ej: Dev seguro"
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </div>

        <div className="space-y-1">
          <span className="text-sm font-medium">Runners asignados</span>
          <div className="grid max-h-32 gap-1 overflow-y-auto rounded border border-border p-2">
            {runners.map((runner) => (
              <label key={runner.agent_id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={runnerIds.includes(runner.agent_id)}
                  onChange={(e) =>
                    setRunnerIds((prev) =>
                      e.target.checked ? [...prev, runner.agent_id] : prev.filter((id) => id !== runner.agent_id),
                    )
                  }
                />
                {runner.name}
                <span
                  className={`text-xs ${runner.status === 'online' ? 'text-green-600' : 'text-muted-foreground'}`}
                >
                  ({runner.status})
                </span>
              </label>
            ))}
          </div>
        </div>

        <RunnerPolicyForm value={config} onChange={setConfig} layer="profile" />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saveProfile.isPending}>
            {saveProfile.isPending ? 'Guardando…' : 'Guardar perfil'}
          </Button>
        </div>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
